/**
 * Лента времени: окна работ внутри сезона.
 *
 * Единственная временная форма, разрешённая продукту сейчас. Всё остальное про
 * время — спарклайн, тренд, продолжение линии вправо — запрещено до появления
 * временного агрегата: ряда, по которому можно было бы говорить о динамике, в
 * корпусе нет, и рисовать его значило бы выдать интерполяцию за наблюдение.
 *
 * Что закреплено формой:
 *
 * * **ось одна — время.** Величины на ленту не наносятся: длина полосы это
 *   длина окна, а не объём работы;
 * * **вправо за данные лента не продолжается.** Домен кончается там, где
 *   кончается последнее известное окно (или дата среза, если она позже);
 * * **линия даты — это `as_of`, а не «сегодня».** Дата названа вслух рядом с
 *   линией. На прошлом срезе сегодняшняя дата была бы чужим числом;
 * * **неполный срок полосой не рисуется.** Окно с одним известным концом
 *   растянулось бы на весь домен и читалось бы как «работа идёт весь сезон»;
 *   вместо полосы стоит причина словами;
 * * **доступность — не отрезок.** Работа открывается решением, а не датой,
 *   поэтому доступность стоит отметкой у строки и на ось не наносится.
 */

import { useRef, type ReactNode } from "react";
import { ChartFrame, ChartTip, chartStyles as styles, useChartTooltip } from "./ChartFrame";
import { formatDate } from "../lib/format";
import { monthShort } from "../astra/lib/format";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";

const m = defineMessages({
  noDeadline: { id: "common.kit.timeline.noDeadline", defaultMessage: "срока нет", description: "Подсказка над полосой ленты сроков, когда у строки нет окна" },
  noBand: { id: "common.kit.timeline.noBand", defaultMessage: "срок не назначен — полосы нет", description: "Вместо полосы на ленте сроков: окно работы не назначено (узкое место справа от подписи)" },
  windowHeader: { id: "common.kit.timeline.windowHeader", defaultMessage: "Окно", description: "Заголовок колонки таблицы-двойника ленты сроков: окно работы «с — по»" },
  noteHeader: { id: "common.kit.timeline.noteHeader", defaultMessage: "Оговорка", description: "Заголовок колонки таблицы-двойника ленты сроков: пометка к строке" },
  noDeadlineCell: { id: "common.kit.timeline.noDeadlineCell", defaultMessage: "срок не назначен", description: "Ячейка таблицы-двойника ленты сроков, когда окна нет" },
  unit: { id: "common.kit.timeline.unit", defaultMessage: "даты", description: "Единица измерения ленты сроков в шапке («даты · {знаменатель}»); строчная, одно слово" },
  emptyAll: { id: "common.kit.timeline.emptyAll", defaultMessage: "Ни у одной строки нет полного окна: ленту строить не по чему. Это отсутствие сроков, а не пустой сезон.", description: "Пустое состояние ленты сроков: ни у одной работы нет окна с началом и концом" },
  tableSummary: { id: "common.kit.timeline.tableSummary", defaultMessage: "Те же окна датами, включая строки без полосы", description: "Подпись к кнопке «таблица» над лентой сроков" },
  noAsOf: { id: "common.kit.timeline.noAsOf", defaultMessage: "среза нет", description: "Подпись под лентой сроков, когда даты среза нет и вертикальной линии не будет" },
  asOfLine: { id: "common.kit.timeline.asOfLine", defaultMessage: "вертикальная линия — дата среза", description: "Пояснение под лентой сроков: что означает вертикальная линия" },
  windowPeriod: { id: "common.kit.timeline.windowPeriod", defaultMessage: "{from} — {to}", description: "Окно дат «с — по»; {from} и {to} — готовые даты" },
  tickYear: { id: "common.kit.timeline.tickYear", defaultMessage: "{month} {year}", description: "Подпись января на шкале ленты сроков: короткий месяц и год («янв 2026»)" },
  bandLabel: { id: "common.kit.timeline.bandLabel", defaultMessage: "{label}: {period}", description: "aria-label полосы окна на ленте сроков; {label} — имя строки, {period} — окно датами" },
});

const windowOf = (from: string, to: string) => tr(m.windowPeriod, { from: formatDate(from), to: formatDate(to) });

export interface TimelineBand {
  key: string;
  /** Начало окна, ISO-дата. */
  from: string;
  /** Конец окна, ISO-дата. */
  to: string;
  /** `window` — окно работ, `muted` — нейтральный отрезок отсутствия. */
  tone?: "window" | "muted";
}

export interface TimelineRow {
  key: string;
  /** Подпись строки: имя кампании или работы, обычно ссылка. */
  label: ReactNode;
  /** То же имя простым текстом — для таблицы и подсказки. */
  labelText: string;
  /** Отметки состояния рядом с именем: они НЕ откладываются по оси. */
  marks?: ReactNode;
  bands: ReadonlyArray<TimelineBand>;
  /** Почему полосы нет: «без срока», «срок не посчитан». Обязательно, если её нет. */
  absent?: string;
  /** Оговорка строки: она же уходит в подсказку. */
  note?: ReactNode;
}

const DAY_MS = 86_400_000;

/** Дата в миллисекундах или `null`, если источник дал не дату. */
function parseDay(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

interface Domain {
  from: number;
  to: number;
  span: number;
}

/** Доля позиции внутри домена: 0 — левый край, 1 — правый. */
function at(domain: Domain, time: number): number {
  return Math.max(0, Math.min(1, (time - domain.from) / domain.span));
}

/** Отметки шкалы — первые числа месяцев внутри домена, не чаще одной в месяц. */
function monthTicks(domain: Domain): { key: string; label: string; left: number }[] {
  const ticks: { key: string; label: string; left: number }[] = [];
  const cursor = new Date(domain.from);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  // Первое число месяца, с которого начинается домен, может лежать левее его
  // начала — тогда отметка сдвигается на следующий месяц.
  if (cursor.getTime() < domain.from) cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor.getTime() <= domain.to && ticks.length < 24) {
    const time = cursor.getTime();
    const month = monthShort(cursor);
    ticks.push({
      key: cursor.toISOString(),
      // Год подписывается только на январе: повторять его у каждого месяца
      // значит заполнить шкалу одинаковыми числами.
      label:
        cursor.getUTCMonth() === 0
          ? tr(m.tickYear, { month, year: String(cursor.getUTCFullYear()) })
          : month,
      left: at(domain, time) * 100,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

function TimeRow({
  row,
  domain,
  ticks,
  asOfLeft,
}: {
  row: TimelineRow;
  domain: Domain;
  /** Волосяная сетка месяцев — та же, что подписана на шкале снизу. */
  ticks: ReadonlyArray<{ key: string; left: number }>;
  /** Позиция линии даты среза в процентах. `null` — среза нет. */
  asOfLeft: number | null;
}) {
  const t = useT();
  const tooltip = useChartTooltip();
  const trackRef = useRef<HTMLDivElement>(null);

  const spans = row.bands
    .map((band) => {
      const from = parseDay(band.from);
      const to = parseDay(band.to);
      if (from === null || to === null) return null;
      const left = at(domain, from) * 100;
      const right = at(domain, Math.max(to, from)) * 100;
      return { band, left, width: Math.max(right - left, 0.4) };
    })
    .filter((span): span is NonNullable<typeof span> => span !== null);

  const period = row.bands
    .map((band) => windowOf(band.from, band.to))
    .join("; ");

  const show = () => {
    const anchor = trackRef.current;
    if (!anchor) return;
    tooltip.show(
      <ChartTip
        value={period || (row.absent ?? t(m.noDeadline))}
        label={row.labelText}
        note={row.note}
      />,
      anchor,
    );
  };

  return (
    <li className={styles.timeRow}>
      <span className={styles.timeLabel}>
        {row.label}
        {row.marks}
      </span>
      {spans.length === 0 ? (
        <span className={styles.timeAbsent}>
          {row.absent ?? t(m.noBand)}
        </span>
      ) : (
        <div
          className={styles.timeTrack}
          ref={trackRef}
          tabIndex={0}
          onPointerEnter={show}
          onFocus={show}
          onPointerLeave={tooltip.hide}
          onBlur={tooltip.hide}
          aria-label={t(m.bandLabel, { label: row.labelText, period })}
        >
          {/* Сетка сплошная и волосяная: пунктир читался бы как «прогноз»,
              а прогноза здесь нет и быть не может. */}
          <span className={styles.timeGrid} aria-hidden>
            {ticks.map((tick) => (
              <span
                key={tick.key}
                className={styles.timeGridLine}
                style={{ left: `${tick.left}%` }}
              />
            ))}
            {asOfLeft === null ? null : (
              <span className={styles.asOfLine} style={{ left: `${asOfLeft}%` }} />
            )}
          </span>
          {spans.map((span) => (
            <div
              key={span.band.key}
              className={`${styles.timeBand} ${
                span.band.tone === "muted" ? styles.timeBandMuted : ""
              }`}
              style={{ left: `${span.left}%`, width: `${span.width}%` }}
            />
          ))}
        </div>
      )}
    </li>
  );
}

export function Timeline({
  title,
  denominator,
  caption,
  epistemic,
  demo,
  rows,
  asOf,
  asOfAbsent,
  legend,
  empty,
}: {
  title: ReactNode;
  /** От чего считаем: «кампаний среза», «работ цикла». */
  denominator: string;
  caption: ReactNode;
  epistemic?: string | null;
  demo?: boolean;
  rows: ReadonlyArray<TimelineRow>;
  /**
   * Дата среза, на которую посчитан экран. Именно она рисуется линией — не
   * «сегодня». `null` — среза нет, и линии не будет.
   */
  asOf: string | null;
  /** Почему линии даты нет. Показывается вместо неё, если `asOf` пуст. */
  asOfAbsent?: string;
  legend?: ReadonlyArray<{ key: string; label: string; kind?: "series" | "muted"; note?: ReactNode }>;
  /** Что сказать, когда ни одного датированного окна нет. */
  empty?: ReactNode;
}) {
  const t = useT();
  const points: number[] = [];
  for (const row of rows) {
    for (const band of row.bands) {
      const from = parseDay(band.from);
      const to = parseDay(band.to);
      if (from !== null) points.push(from);
      if (to !== null) points.push(to);
    }
  }
  const asOfTime = parseDay(asOf);
  if (asOfTime !== null && points.length > 0) points.push(asOfTime);

  const table = (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">{denominator}</th>
          <th scope="col">{t(m.windowHeader)}</th>
          <th scope="col">{t(m.noteHeader)}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">{row.label}</th>
            <td>
              {row.bands.length > 0
                ? row.bands
                    .map((band) => windowOf(band.from, band.to))
                    .join("; ")
                : (row.absent ?? t(m.noDeadlineCell))}
            </td>
            <td>{row.note ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (points.length === 0) {
    return (
      <ChartFrame
        title={title}
        unit={t(m.unit)}
        denominator={denominator}
        caption={caption}
        epistemic={epistemic}
        demo={demo}
        table={table}
        legend={legend}
      >
        <p className={styles.timeAbsent}>
          {empty ?? t(m.emptyAll)}
        </p>
      </ChartFrame>
    );
  }

  const from = Math.min(...points);
  // Домен кончается там, где кончаются данные. Вправо «на будущее» лента не
  // продолжается: за последним известным окном ничего не наблюдалось.
  const to = Math.max(...points);
  const domain: Domain = { from, to, span: Math.max(to - from, DAY_MS) };
  const ticks = monthTicks(domain);
  const asOfLeft = asOfTime === null ? null : at(domain, asOfTime) * 100;

  return (
    <ChartFrame
      title={title}
      unit={t(m.unit)}
      denominator={denominator}
      caption={caption}
      epistemic={epistemic}
      demo={demo}
      table={table}
      legend={legend}
      tableSummary={t(m.tableSummary)}
    >
      <ul className={styles.timeline}>
        {rows.map((row) => (
          <TimeRow
            key={row.key}
            row={row}
            domain={domain}
            ticks={ticks}
            asOfLeft={asOfLeft}
          />
        ))}
      </ul>

      <div className={styles.scale}>
        <span>
          {asOfTime === null
            ? (asOfAbsent ?? t(m.noAsOf))
            : t(m.asOfLine)}
        </span>
        <span className={styles.scaleTrack}>
          {ticks.map((tick) => (
            <span
              key={tick.key}
              className={styles.scaleTick}
              style={{ left: `${tick.left}%` }}
            >
              {tick.label}
            </span>
          ))}
          {asOfLeft === null ? null : (
            <span className={styles.asOfChip} style={{ left: `${asOfLeft}%` }}>
              {formatDate(asOf)}
            </span>
          )}
        </span>
      </div>
    </ChartFrame>
  );
}
